import { useCallback, useState } from "react";

import { useAppPartySocket } from "~/app/hooks/useAppPartySocket";

import type { PartyServerConnectionStatus } from "../components/Call/hooks/useCallDisplay";

export function usePartySocketWithReconnect({
  room,
  party,
  onMessage,
}: {
  room: string;
  party: string;
  onMessage: (event: MessageEvent) => void;
}) {
  const [partyServerConnectionStatus, setPartyServerConnectionStatus] =
    useState<PartyServerConnectionStatus>("disconnected");
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const socket = useAppPartySocket({
    room,
    party,
    onOpen() {
      console.log("WebSocket connection opened for room:", room);
      setPartyServerConnectionStatus("connected");
      setReconnectAttempt(0);
    },
    onClose() {
      console.warn("WebSocket connection closed for room:", room);
      setPartyServerConnectionStatus("disconnected");
      handleReconnect();
    },
    onError(error) {
      console.error("WebSocket error for room:", room, error);
    },
    onMessage,
  });

  const handleReconnect = useCallback(() => {
    setPartyServerConnectionStatus("reconnecting");
    const backoffTime = Math.min(1000 * 2 ** reconnectAttempt, 30000); // Max 30 seconds
    setTimeout(() => {
      setReconnectAttempt((prev) => prev + 1);
      socket.reconnect();
    }, backoffTime);
  }, [socket, reconnectAttempt]);

  return {
    socket,
    partyServerConnectionStatus,
    reconnectAttempt,
  };
}
